import { useRef, useState } from 'react';
import { Customer } from '@api/Customers/Customer';
import { DiscountType, PromotionalCode } from '@api/PromotionalCodes/PromotionalCode';
import { ShoppingCartLineV2, ShoppingCartV2 } from '@api/ShoppingCart/ShoppingCartV2';

/**
 * El CARRITO del POS: las líneas, el dinero y la aritmética que sale de
 * ellos. Extraído de `POSScreen` en F4 del PLAN-REDISENO-PANEL-POS-Y-RECIBO
 * (§7.0) SIN cambiar una sola cuenta.
 *
 * Por qué tiene tests y el resto del refactor no: de esta aritmética dependen
 * los PUNTOS (el rebuild de carteras recalcula desde `orders`, así que un
 * total mal escrito no se nota hasta que el cliente reclama su saldo) y el
 * CORTE CIEGO de caja (`cashAmount` es lo que debe estar físicamente en el
 * cajón). Las funciones de abajo son puras a propósito: se prueban sin React,
 * igual que `useAppUpdateChecker`.
 */

// ----- Aritmética PURA -----
// Cada línea v2 guarda en `cost` el TOTAL de la línea (cantidad y extras ya
// incluidos), no el precio unitario — misma convención que la orden persistida.

export const getCartSubtotal = (cart?: ShoppingCartV2): number =>
    cart?.products?.reduce((total, line) => total + line.cost, 0) || 0;

export const getCartPromotion = (cart?: ShoppingCartV2): number => {
    const subTotal = getCartSubtotal(cart);
    let discount = 0;
    if (cart?.promotionalCode) {
        if (cart.promotionalCode.discountType === DiscountType.Percent) {
            discount = subTotal * (Number(cart.promotionalCode.discountValue) / 100);
        } else {
            discount = Number(cart.promotionalCode.discountValue);
        }
    }
    return discount;
};

export const getCartTotal = (cart: ShoppingCartV2 | undefined, rewardAmount: number): number =>
    getCartSubtotal(cart) - getCartPromotion(cart) - rewardAmount + (cart?.shippingCost || 0);

/** Lo ya registrado por cada medio de pago, más la propina. */
export interface PosPayments {
    tipAmount: number;
    creditCardAmount: number;
    bankTransferAmount: number;
    cashRegisteredAmount: number;
}

/**
 * D2: lo cobrado = total + propina. El efectivo es el residuo, propina
 * incluida — así `cashAmount` refleja lo que físicamente entra al cajón y el
 * corte ciego cuadra sin tocar sus fórmulas. La propina NO entra a `total`
 * (no es venta y no da puntos).
 */
export const getCartTotalCash = (
    cart: ShoppingCartV2 | undefined,
    rewardAmount: number,
    payments: PosPayments
): number =>
    getCartTotal(cart, rewardAmount) + payments.tipAmount - payments.creditCardAmount - payments.bankTransferAmount;

/** Lo que FALTA por cobrar (el efectivo ya registrado en parcialidades cuenta). */
export const getCartRemainingToCollect = (
    cart: ShoppingCartV2 | undefined,
    rewardAmount: number,
    payments: PosPayments
): number => getCartTotalCash(cart, rewardAmount, payments) - payments.cashRegisteredAmount;

/** Lo que hay que devolver al retomar un pedido: la cuenta COMPLETA, no solo las líneas. */
export interface PosCartRestore {
    products: ShoppingCartLineV2[];
    customer?: Customer;
    note?: string;
    promotionalCode?: PromotionalCode;
    shippingCost?: number;
    pointsAmount: number;
    /** Propina ya presente en la orden. Omitir = no tocar la propina actual. */
    tipAmount?: number;
}

const usePosCart = () => {
    const [shoppingCart, setShoppingCart] = useState<ShoppingCartV2 | undefined>(undefined);
    const [creditCardAmount, setCreditCardAmount] = useState<number>(0);
    const [bankTransferAmount, setBankTransferAmount] = useState<number>(0);
    // Efectivo REGISTRADO en parcialidades (pago libre, QA 2026-08-26): baja el
    // restante a cobrar pero NO cambia lo persistido — cashAmount sigue siendo
    // el residuo total + propina − tarjeta − transferencia (todo lo que no fue
    // tarjeta/transferencia ES efectivo, registrado o final).
    const [cashRegisteredAmount, setCashRegisteredAmount] = useState<number>(0);
    const [rewardAmount, setRewardAmount] = useState<number>(0);
    // Propina (F5/D2): estado para pintar + ref para la lógica del cobro — el
    // confirm de la propina puede desembocar DIRECTO en crear/completar la
    // orden y el estado de React aún no está comprometido en ese instante.
    const [tipAmount, setTipAmount] = useState<number>(0);
    const tipAmountRef = useRef<number>(0);

    const applyTip = (amount: number) => {
        tipAmountRef.current = amount;
        setTipAmount(amount);
    };

    // ⚠️ La propina se lee del REF, nunca del estado: ver el comentario de arriba.
    const payments = (): PosPayments => ({
        tipAmount: tipAmountRef.current,
        creditCardAmount,
        bankTransferAmount,
        cashRegisteredAmount
    });

    const getSubtotal = (): number => getCartSubtotal(shoppingCart);
    const getPromotion = (): number => getCartPromotion(shoppingCart);
    const getTotal = (): number => getCartTotal(shoppingCart, rewardAmount);
    const getTotalCash = (): number => getCartTotalCash(shoppingCart, rewardAmount, payments());
    const getRemainingToCollect = (): number => getCartRemainingToCollect(shoppingCart, rewardAmount, payments());

    // ----- Líneas -----
    // Muta el arreglo y crea un carrito nuevo (misma mecánica de siempre: la
    // identidad que cambia es la del carrito, no la del arreglo).
    const addLine = (line: ShoppingCartLineV2) => {
        const products = shoppingCart?.products || [];
        if (typeof line.id === 'undefined') {
            line.id = products.length;
            products.push(line);
        } else {
            products[line.id] = line;
        }
        setShoppingCart({ ...shoppingCart, products });
    };

    const removeLine = (index: number) => {
        const products = shoppingCart?.products || [];
        products.splice(index, 1);
        setShoppingCart({
            ...shoppingCart,
            products: products.map((line, idx) => ({ ...line, id: idx }))
        });
    };

    // ----- Cliente, nota, promoción y envío -----
    const setCustomer = (customer: Customer) => setShoppingCart({ ...shoppingCart, customer });

    const setCustomerName = (name: string) => setShoppingCart({ ...shoppingCart, customer: { profile: { name } } });

    /**
     * Quitar al cliente del pedido. ⚠️ Resetea los puntos SIEMPRE: el canje era
     * de ESE cliente y dejarlo cobraría un descuento de puntos que ya no le
     * pertenece a nadie. Se deja un walk-in en blanco (no `undefined`) para que
     * el campo de nombre siga ahí y el cajero pueda teclear a quién se le vende.
     */
    const clearCustomer = () => {
        setRewardAmount(0);
        setShoppingCart({ ...shoppingCart, customer: { profile: { name: '' } } });
    };

    const setNote = (note?: string) => setShoppingCart({ ...shoppingCart, note });

    // El código de promoción y el canje de puntos no se acumulan (regla vieja del POS)
    const applyPromotionalCode = (promotionalCode: PromotionalCode) => {
        setRewardAmount(0);
        setShoppingCart({ ...shoppingCart, promotionalCode });
    };

    const removePromotionalCode = () => setShoppingCart({ ...shoppingCart, promotionalCode: undefined });

    const setShippingCost = (shippingCost?: number) => setShoppingCart({ ...shoppingCart, shippingCost });

    // ----- Pagos -----
    // PAGO LIBRE: efectivo, tarjeta y transferencia se registran en cualquier
    // orden y combinación, y se DESCUENTAN en el resumen del pedido.
    const registerPayment = (type: 'cash' | 'card' | 'transfer', amount: number) => {
        if (type === 'card') {
            setCreditCardAmount((prev) => prev + amount);
        } else if (type === 'transfer') {
            setBankTransferAmount((prev) => prev + amount);
        } else {
            setCashRegisteredAmount((prev) => prev + amount);
        }
    };

    /**
     * Retomar un pedido devuelve la cuenta COMPLETA (bug de 2026-08-26): se
     * restauraban productos/cliente/nota pero NO los puntos, la promoción ni el
     * envío, así que `getTotal()` volvía a ser el subtotal — de ahí salía el %
     * de propina, y al cobrar se pisaba `pointsAmount` con 0 (el cliente pagaba
     * completo el descuento que ya había aplicado).
     *
     * La propina NO se restaura: siempre viene del espejo (el cliente la
     * confirma en su pantalla) y el efecto que la espeja la pisaría un instante
     * después. Además una orden "por pagar" se guarda con tipAmount 0 por diseño.
     */
    const restore = (data: PosCartRestore) => {
        setRewardAmount(data.pointsAmount);
        // Propina que ya venía en la orden (la mesa la deja el cliente desde su
        // teléfono al pedir la cuenta, F4). `undefined` = no restaurar nada.
        if (typeof data.tipAmount === 'number') applyTip(data.tipAmount);
        setShoppingCart({
            products: data.products,
            customer: data.customer,
            note: data.note,
            promotionalCode: data.promotionalCode,
            shippingCost: data.shippingCost
        });
    };

    /** La parte del carrito de `onClean`: pedido nuevo, walk-in y cero dinero. */
    const reset = () => {
        setShoppingCart({ customer: { profile: { name: `0000` } } });
        setCreditCardAmount(0);
        setBankTransferAmount(0);
        setCashRegisteredAmount(0);
        setRewardAmount(0);
        applyTip(0);
    };

    return {
        // Estado
        shoppingCart,
        setShoppingCart,
        rewardAmount,
        setRewardAmount,
        creditCardAmount,
        bankTransferAmount,
        cashRegisteredAmount,
        tipAmount,
        tipAmountRef,
        applyTip,
        // Aritmética
        getSubtotal,
        getPromotion,
        getTotal,
        getTotalCash,
        getRemainingToCollect,
        // Líneas
        addLine,
        removeLine,
        // Cliente, nota, promoción y envío
        setCustomer,
        setCustomerName,
        clearCustomer,
        setNote,
        applyPromotionalCode,
        removePromotionalCode,
        setShippingCost,
        // Pagos
        registerPayment,
        removeCreditCard: () => setCreditCardAmount(0),
        removeBankTransfer: () => setBankTransferAmount(0),
        removeCashRegistered: () => setCashRegisteredAmount(0),
        removePoints: () => setRewardAmount(0),
        // Ciclo de vida del pedido
        restore,
        reset
    };
};

/** El carrito completo, tal como lo consumen la pantalla y `usePosSessionMirror`. */
export type PosCart = ReturnType<typeof usePosCart>;

export default usePosCart;
